import { supabase } from '../../../foundation/altzero_base/base/utils/supabaseClient';
import type { ApiResponse } from '../types/billValidationTypes';
import type { BvBill } from '../types/billTypes';
import type { BvBillRun } from '../lib/billRunLog';
import { foldSendHistory, type SendHistory, type SendRowLike } from '../lib/reportStatus';
import { billService } from './billService';
import type { InvoiceEmailLike } from '../utils/billParseStatus';
import type { QueueLike, AnalysisLike } from '../lib/billPipeline';

// Bulk Bill Run + Bulk Report. Straight Supabase reads: RLS scopes the run log
// (migration 118) and bv_report_send (083) to the viewer's organisation, so no
// server route is needed.

const PAGE_SIZE = 1000;
/** Bill ids per `.in()` filter, so the request URL stays short. */
const BILL_ID_CHUNK = 100;
const API_BASE = import.meta.env.VITE_API_URL ?? 'http://localhost:3001';

export type RunNowResult = {
  runId: string | null;
  started: string[];
  skipped: Array<{ bill_id: string; reason: string }>;
};

export type RunProgress = {
  status: BvBillRun['status'] | null;
  items: Array<{ bill_id: string; outcome: 'done' | 'error' | 'blocked'; reason: string | null }>;
};

/**
 * One duplicated bill period, as migration 128 plans it. `kind`: A one bill
 * holds the invoice and the rest are empty; B the same invoice total on
 * several; D none has an invoice; C the invoices differ, so a person chooses
 * (keepId is null).
 */
export type DuplicatePeriod = {
  siteId: string;
  billFrom: string;
  billTo: string;
  kind: 'A' | 'B' | 'C' | 'D';
  keepId: string | null;
  billIds: string[];
  resolved: boolean;
};

type DuplicateRow = {
  site_id: string; bill_from: string; bill_to: string; kind: DuplicatePeriod['kind'];
  keep_id: string | null; bill_ids: string[]; resolved: boolean;
};

const toDuplicate = (r: DuplicateRow): DuplicatePeriod => ({
  siteId: r.site_id, billFrom: r.bill_from, billTo: r.bill_to, kind: r.kind,
  keepId: r.keep_id, billIds: r.bill_ids ?? [], resolved: r.resolved,
});

function fail<T>(error: unknown): ApiResponse<T> {
  return { success: false, error: error instanceof Error ? error.message : String((error as { message?: string })?.message ?? error) };
}

/** Same columns as `billService.listAll`, so a re-read bill renders identically. */
const BILL_SELECT = '*, site:site_id(nmi, bv_extn, addr_street, addr_city, addr_state, addr_postcode, current_retailer, drive_link, client:client_id(name, drive_link))';

/** expected_total / invoice_total: what each sent report stated (migration 125). */
const SEND_COLUMNS = 'id, bill_id, status, sent_at, created_at, error, expected_total, invoice_total';

async function currentOrgId(): Promise<string | null> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) return null;
  const { data } = await supabase
    .from('base_organisation_members')
    .select('organisation_id')
    .eq('user_id', user.id)
    .limit(1)
    .maybeSingle();
  return (data?.organisation_id as string | undefined) ?? null;
}

/** Every row of an org-scoped read, paged — an unbounded select stops at 1000 silently. */
async function pagedByOrg<T>(
  table: string,
  columns: string,
  orgId: string,
  orderBy: string,
): Promise<T[]> {
  const all: T[] = [];
  for (let page = 0; ; page++) {
    const { data, error } = await supabase
      .from(table)
      .select(columns)
      // Explicit even though RLS already scopes the rows — cheap, and it keeps
      // the read correct under a role that bypasses RLS.
      .eq('org_id', orgId)
      .order(orderBy, { ascending: true })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
    if (error) throw error;
    all.push(...((data ?? []) as T[]));
    if (!data || data.length < PAGE_SIZE) break;
  }
  return all;
}

export type PipelineQueueRow = QueueLike & { bill_id: string };

/**
 * Usage-data coverage for the caller's unpriced bills (migration 123). Not the
 * bv_bill_run_queue view: that counts interval days for every bill and, read as
 * the signed-in user under RLS, ran past the 8 s statement timeout. A priced bill
 * passes the usage stage without coverage, so only unpriced ones are counted.
 */
async function boardCoverage(): Promise<PipelineQueueRow[]> {
  const all: PipelineQueueRow[] = [];
  for (let page = 0; ; page++) {
    const { data, error } = await supabase
      .rpc('bv_board_coverage')
      .order('bill_id', { ascending: true })
      .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
    if (error) throw error;
    all.push(...((data ?? []) as PipelineQueueRow[]));
    if (!data || data.length < PAGE_SIZE) break;
  }
  return all;
}
export type PipelineAnalysisRow = AnalysisLike & {
  bill_id: string | null;
  site_id: string;
  period_from: string;
  period_to: string;
};

export type PipelineData = {
  bills: BvBill[];
  emails: InvoiceEmailLike[];
  queue: PipelineQueueRow[];
  analyses: PipelineAnalysisRow[];
  sendHistory: Record<string, SendHistory>;
  lastRun: BvBillRun | null;
};

export const bulkReportService = {
  /**
   * Everything the Bulk Bill Run board needs to place every active bill in the
   * pipeline. Read in parallel; the stage logic is `lib/billPipeline`.
   */
  loadPipeline: async (): Promise<ApiResponse<PipelineData>> => {
    try {
      const orgId = await currentOrgId();
      if (!orgId) return { success: false, error: 'Organization not found' };

      const [billRes, emailRes, queue, analyses, sentRes, runRes] = await Promise.all([
        billService.listAll(),
        billService.listInvoiceEmails(),
        boardCoverage(),
        pagedByOrg<PipelineAnalysisRow>(
          'bv_nem_analysis',
          'id, bill_id, site_id, period_from, period_to, data_status, data_error, tariff_status, tariff_error, tou_status, tou_error',
          orgId, 'id'),
        bulkReportService.sendHistoryByBill(),
        bulkReportService.listRuns(1),
      ]);
      if (!billRes.success) throw new Error(billRes.error ?? 'Could not load bills');
      if (!emailRes.success) throw new Error(emailRes.error ?? 'Could not load inbox emails');

      return {
        success: true,
        data: {
          bills: billRes.data ?? [],
          emails: emailRes.data ?? [],
          queue,
          analyses,
          // Secondary: without these the board still shows every stage.
          sendHistory: sentRes.success ? sentRes.data ?? {} : {},
          lastRun: runRes.success ? runRes.data?.[0] ?? null : null,
        },
      };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  /**
   * Bill run (usage, network, TOU, calculation) now, for the selected bills (server: POST
   * /bill-runs/run). Resolves when queued; bills whose usage data does not cover
   * their period come back in `skipped` with the reason and are not calculated.
   */
  runBillsNow: async (billIds: string[]): Promise<ApiResponse<RunNowResult>> => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(`${API_BASE}/api/bill-validation/bill-runs/run`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'x-api-key': 'scopingai',
          ...(session?.user?.id ? { 'x-user-id': session.user.id } : {}),
          ...(session?.access_token ? { Authorization: `Bearer ${session.access_token}` } : {}),
        },
        body: JSON.stringify({ billIds }),
      });
      const json = await res.json() as { success: boolean; data?: RunNowResult; error?: string };
      if (!json.success || !json.data) return { success: false, error: json.error ?? 'Could not start the run' };
      return { success: true, data: json.data };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  /** A run's status and the bills it has finished so far (run log, RLS-scoped). */
  runProgress: async (runId: string): Promise<ApiResponse<RunProgress>> => {
    try {
      const [{ data: run, error: runErr }, { data: items, error: itemErr }] = await Promise.all([
        supabase.from('bv_bill_run').select('status').eq('id', runId).maybeSingle(),
        supabase.from('bv_bill_run_item').select('bill_id, outcome, reason').eq('run_id', runId),
      ]);
      if (runErr) throw runErr;
      if (itemErr) throw itemErr;
      return {
        success: true,
        data: {
          status: (run?.status as BvBillRun['status'] | undefined) ?? null,
          items: (items ?? []) as RunProgress['items'],
        },
      };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  /** The most recent nightly runs, newest first. */
  listRuns: async (limit = 30): Promise<ApiResponse<BvBillRun[]>> => {
    try {
      const { data, error } = await supabase
        .from('bv_bill_run')
        .select('id, started_at, finished_at, status, error')
        .order('started_at', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return { success: true, data: (data ?? []) as BvBillRun[] };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  /** Bills a run created reports for — what "Reports from this run" narrows to. */
  runReportBillIds: async (runId: string): Promise<ApiResponse<{ startedAt: string | null; billIds: string[] }>> => {
    try {
      const [{ data: run }, { data, error }] = await Promise.all([
        supabase.from('bv_bill_run').select('started_at').eq('id', runId).maybeSingle(),
        supabase.from('bv_bill_run_item').select('bill_id').eq('run_id', runId).eq('report_created', true),
      ]);
      if (error) throw error;
      return {
        success: true,
        data: { startedAt: run?.started_at ?? null, billIds: (data ?? []).map((r) => r.bill_id as string) },
      };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  /**
   * Every bill's send history — latest success and latest attempt of either
   * outcome, with its error. Paged: an unbounded select stops at 1000 silently.
   */
  sendHistoryByBill: async (): Promise<ApiResponse<Record<string, SendHistory>>> => {
    try {
      const rows: SendRowLike[] = [];
      for (let page = 0; ; page++) {
        const { data, error } = await supabase
          .from('bv_report_send')
          .select(SEND_COLUMNS)
          .not('bill_id', 'is', null)
          .order('id', { ascending: true })
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (error) throw error;
        rows.push(...((data ?? []) as SendRowLike[]));
        if (!data || data.length < PAGE_SIZE) break;
      }
      return { success: true, data: foldSendHistory(rows) };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  /**
   * Current bills with their send history, re-read for a bulk send. The page's
   * copies may predate a re-run; the send must use — and re-check — what is in
   * the database now.
   */
  getBillsForSend: async (
    billIds: string[],
  ): Promise<ApiResponse<{ bills: BvBill[]; history: Record<string, SendHistory> }>> => {
    if (billIds.length === 0) return { success: true, data: { bills: [], history: {} } };
    try {
      // Chunked so a large selection never overflows the request URL.
      const bills: BvBill[] = [];
      const sends: SendRowLike[] = [];
      for (let i = 0; i < billIds.length; i += BILL_ID_CHUNK) {
        const ids = billIds.slice(i, i + BILL_ID_CHUNK);
        const [{ data, error }, { data: sendRows, error: sendErr }] = await Promise.all([
          // Archived since the page loaded = gone: an archived bill must not be sent.
          supabase.from('bv_bill').select(BILL_SELECT).in('id', ids).is('archived_at', null),
          supabase.from('bv_report_send').select(SEND_COLUMNS).in('bill_id', ids),
        ]);
        if (error) throw error;
        if (sendErr) throw sendErr;
        bills.push(...((data ?? []) as BvBill[]));
        sends.push(...((sendRows ?? []) as SendRowLike[]));
      }
      return { success: true, data: { bills, history: foldSendHistory(sends) } };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  /**
   * A report batch's bills, for its MultiSite export: the item bill ids in
   * batch order, and each bill with the same columns Bill Runs reads (site,
   * client, invoice). Archived bills are kept — the batch did cover them.
   * RLS (migration 148) scopes batch items to the viewer's organisation.
   */
  batchBills: async (batchId: string): Promise<ApiResponse<{ billIds: string[]; bills: BvBill[] }>> => {
    try {
      const billIds: string[] = [];
      for (let page = 0; ; page++) {
        const { data, error } = await supabase
          .from('bv_report_batch_item')
          .select('bill_id')
          .eq('batch_id', batchId)
          .order('position', { ascending: true })
          .range(page * PAGE_SIZE, (page + 1) * PAGE_SIZE - 1);
        if (error) throw error;
        billIds.push(...(data ?? []).map((r) => r.bill_id as string));
        if (!data || data.length < PAGE_SIZE) break;
      }
      const bills: BvBill[] = [];
      for (let i = 0; i < billIds.length; i += BILL_ID_CHUNK) {
        const { data, error } = await supabase
          .from('bv_bill').select(BILL_SELECT).in('id', billIds.slice(i, i + BILL_ID_CHUNK));
        if (error) throw error;
        bills.push(...((data ?? []) as BvBill[]));
      }
      return { success: true, data: { billIds, bills } };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  /**
   * "Keep this one": archives every other active bill for this bill's exact
   * site and period, and moves their inbox emails onto it (migration 128).
   * Returns how many bills were archived.
   */
  resolveDuplicate: async (keepId: string): Promise<ApiResponse<number>> => {
    try {
      const { data, error } = await supabase.rpc('bv_resolve_duplicate_period', { p_keep: keepId });
      if (error) throw error;
      return { success: true, data: Number(data ?? 0) };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  /** Every duplicated period in the viewer's organisation, and what fixing it would keep. */
  planDuplicates: async (): Promise<ApiResponse<DuplicatePeriod[]>> => {
    try {
      const { data, error } = await supabase.rpc('bv_auto_resolve_duplicate_periods', { p_apply: false });
      if (error) throw error;
      return { success: true, data: ((data ?? []) as DuplicateRow[]).map(toDuplicate) };
    } catch (error: unknown) {
      return fail(error);
    }
  },

  /** Resolve the A, B and D periods; C periods come back unresolved for a person. */
  fixObviousDuplicates: async (): Promise<ApiResponse<DuplicatePeriod[]>> => {
    try {
      const { data, error } = await supabase.rpc('bv_auto_resolve_duplicate_periods', { p_apply: true });
      if (error) throw error;
      return { success: true, data: ((data ?? []) as DuplicateRow[]).map(toDuplicate) };
    } catch (error: unknown) {
      return fail(error);
    }
  },
};
